import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Upload, X, Music, Image, Crop } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ImageCropper } from "@/components/ImageCropper";
import { beatUploadSchema, validateImageFile, validateAudioFile, validateProjectFile } from "@/lib/validation";
import { uploadToGCS } from "@/lib/gcsUpload";

interface AddBeatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBeatAdded: () => void;
}

interface FileUpload {
  file: File;
  type: 'mp3' | 'wav' | 'flp' | 'image';
  preview?: string;
  croppedBlob?: Blob;
}

interface DropZoneProps {
  type: 'audio' | 'image' | 'flp';
  title: string;
  description: string;
  accept: string;
  icon: React.ReactNode;
  files: FileUpload[];
  onDrop: (files: File[], type: FileUpload['type'][]) => void;
  onRemove: (index: number) => void;
  onCropImage?: (index: number, preview: string) => void;
}

const DropZone = ({ type, title, description, accept, icon, files, onDrop, onRemove, onCropImage }: DropZoneProps) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validTypes: FileUpload['type'][] = type === 'audio' ? ['mp3', 'wav'] : type === 'image' ? ['image'] : ['flp'];
    onDrop(droppedFiles, validTypes);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validTypes: FileUpload['type'][] = type === 'audio' ? ['mp3', 'wav'] : type === 'image' ? ['image'] : ['flp'];
    onDrop(selectedFiles, validTypes);
  };

  const filteredFiles = files.filter(f => 
    type === 'audio' ? ['mp3', 'wav'].includes(f.type) :
    type === 'image' ? f.type === 'image' :
    f.type === 'flp'
  );

  return (
    <div className="space-y-3">
      <Label>{title}</Label>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive ? 'border-primary bg-primary/5' : 'border-border'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-2">
          {icon}
          <p className="text-sm font-medium">{description}</p>
          <p className="text-xs text-muted-foreground">
            Drag files here or click to select
          </p>
          <Input
            type="file"
            multiple={type === 'audio'}
            accept={accept}
            onChange={handleFileInput}
            className="hidden"
            id={`file-upload-${type}`}
          />
          <Button 
            type="button"
            variant="outline" 
            size="sm"
            onClick={() => document.getElementById(`file-upload-${type}`)?.click()}
          >
            Choose Files
          </Button>
        </div>
      </div>
      
      {filteredFiles.length > 0 && (
        <div className="grid gap-2">
          {filteredFiles.map((fileUpload, index) => {
            const globalIndex = files.indexOf(fileUpload);
            return (
              <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                {fileUpload.type === 'image' ? <Image className="h-4 w-4" /> : <Music className="h-4 w-4" />}
                <div className="flex-1">
                  <p className="text-sm font-medium">{fileUpload.file.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{fileUpload.type} file</p>
                </div>
                 {fileUpload.preview && (
                  <div className="flex gap-2">
                    <img 
                      src={fileUpload.croppedBlob ? URL.createObjectURL(fileUpload.croppedBlob) : fileUpload.preview} 
                      alt="Preview" 
                      className="w-10 h-10 rounded object-cover" 
                    />
                      {fileUpload.type === 'image' && onCropImage && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onCropImage(globalIndex, fileUpload.preview!);
                          }}
                        >
                          <Crop className="h-4 w-4" />
                        </Button>
                      )}
                  </div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(globalIndex)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const AddBeatModal = ({ isOpen, onClose, onBeatAdded }: AddBeatModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  
  // Form state
  const [title, setTitle] = useState("");
  const [producer, setProducer] = useState("");
  const [genre, setGenre] = useState("");
  const [bpm, setBpm] = useState("");
  const [mp3LeasePrice, setMp3LeasePrice] = useState("25");
  const [wavLeasePrice, setWavLeasePrice] = useState("35");
  const [flpPrice, setFlpPrice] = useState("100");
  const [semiExclusivePrice, setSemiExclusivePrice] = useState("250");
  const [fullExclusivePrice, setFullExclusivePrice] = useState("500");
  const [isFreebeat, setIsFreebeat] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [releaseDate, setReleaseDate] = useState("");
  const [exclusiveReleaseUntil, setExclusiveReleaseUntil] = useState("");
  const { toast } = useToast();

  const processFiles = (newFiles: File[], allowedTypes: FileUpload['type'][]) => {
    const validFiles: FileUpload[] = [];

    newFiles.forEach(file => {
      let type: FileUpload['type'] | null = null;
      
      if (file.name.endsWith('.mp3') && allowedTypes.includes('mp3')) {
        type = 'mp3';
      } else if (file.name.endsWith('.wav') && allowedTypes.includes('wav')) {
        type = 'wav';
      } else if (allowedTypes.includes('flp')) {
        // Accept various project file formats for FLP
        const flpExtensions = ['.flp', '.zip', '.rar', '.7z'];
        const hasFlpExtension = flpExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        if (hasFlpExtension) {
          type = 'flp';
        }
      } else if (allowedTypes.includes('image')) {
        const imageExtensions = ['.png', '.jpg', '.jpeg'];
        const hasImageExtension = imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        if (hasImageExtension && file.type.startsWith('image/')) {
          type = 'image';
        }
      }

      if (type) {
        const fileUpload: FileUpload = { file, type };
        
        if (type === 'image') {
          fileUpload.preview = URL.createObjectURL(file);
        }
        
        validFiles.push(fileUpload);
      }
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const uploadFile = async (fileUpload: FileUpload, retries = 2): Promise<{ url: string; fullUrl?: string } | null> => {
    const { file, type, croppedBlob } = fileUpload;
    
    // Route WAV and FLP files to Google Cloud Storage using chunked uploads
    if (type === 'wav' || type === 'flp') {
      if (import.meta.env.DEV) {
        console.log(`Initiating GCS chunked upload for ${type}:`, file.name, `Size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      }

      const result = await uploadToGCS({
        file,
        fileType: type,
        onProgress: (progress) => {
          if (import.meta.env.DEV) {
            console.log(`[Upload] ${type}: ${progress.percentage}%`);
          }
        },
      });

      if (!result.success) {
        toast({
          title: 'Upload Error',
          description: result.error || `Failed to upload ${type} file to Google Cloud Storage`,
          variant: 'destructive',
        });
        return null;
      }

      if (import.meta.env.DEV) console.log(`GCS upload complete:`, result.url);
      return { url: result.url! };
    }
    
    // Handle Supabase Storage uploads for MP3 and images
    let bucket = '';
    if (type === 'image') {
      bucket = 'beat-images';
    } else if (type === 'mp3') {
      bucket = 'beat-audio';
    }

    // For images, upload both cropped and full versions
    if (type === 'image' && croppedBlob) {
      const baseFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}`;
      
      // Upload cropped version
      const croppedFileName = `${baseFileName}-cropped.jpg`;
      const fullFileName = `${baseFileName}-full.${file.name.split('.').pop()}`;
      
      try {
        // Upload cropped version
        const { data: croppedData, error: croppedError } = await supabase.storage
          .from(bucket)
          .upload(croppedFileName, croppedBlob);
        
        if (croppedError) throw croppedError;
        
        // Upload full version
        const { data: fullData, error: fullError } = await supabase.storage
          .from(bucket)
          .upload(fullFileName, file);
        
        if (fullError) throw fullError;
        
        const { data: { publicUrl: croppedUrl } } = supabase.storage
          .from(bucket)
          .getPublicUrl(croppedFileName);
        
        const { data: { publicUrl: fullUrl } } = supabase.storage
          .from(bucket)
          .getPublicUrl(fullFileName);
        
        return { url: croppedUrl, fullUrl };
      } catch (error) {
        if (import.meta.env.DEV) console.error('Error uploading image files:', error);
        return null;
      }
    }
    
    // For non-image files or images without cropping
    const actualFile = croppedBlob || file;
    const fileExt = croppedBlob ? 'jpg' : file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

    if (import.meta.env.DEV) console.log(`Uploading ${type} file to bucket ${bucket}:`, file.name);

    try {
      // Check and refresh session before upload
      const { data: session, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.session) {
        if (import.meta.env.DEV) console.error('Session error:', sessionError);
        throw new Error('Authentication session expired. Please log in again.');
      }

      // Check if token is close to expiring (within 5 minutes)
      const tokenExp = session.session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenExp ? tokenExp - now : 0;

      if (timeUntilExpiry < 300) { // Less than 5 minutes
        if (import.meta.env.DEV) console.log('Token close to expiry, refreshing...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          if (import.meta.env.DEV) console.error('Failed to refresh session:', refreshError);
          throw new Error('Failed to refresh authentication. Please log in again.');
        }
      }

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, actualFile);

      if (error) {
        // Handle specific authentication errors
        if (error.message?.includes('exp') || error.message?.includes('Unauthorized') || error.message?.includes('403')) {
          if (retries > 0) {
            if (import.meta.env.DEV) console.log(`Authentication error, retrying upload for ${type}. Retries left: ${retries}`);
            // Refresh session and retry
            await supabase.auth.refreshSession();
            return uploadFile(fileUpload, retries - 1);
          } else {
            throw new Error('Authentication failed after retries. Please log in again and try uploading.');
          }
        }
        
        if (import.meta.env.DEV) console.error(`Error uploading ${type}:`, error);
        throw new Error(`Failed to upload ${type} file: ${error.message}`);
      }

      if (import.meta.env.DEV) console.log(`Successfully uploaded ${type}:`, data);

const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      // For private project files (FLP/ZIP/RAR), store a storage reference instead of a public URL
      if (bucket === 'beat-files') {
        return { url: `storage://beat-files/${fileName}` };
      }

      return { url: urlData.publicUrl };
    } catch (error) {
      if (import.meta.env.DEV) console.error(`Upload error for ${type}:`, error);
      
      if (retries > 0 && error instanceof Error) {
        // Only retry on auth errors
        if (error.message.includes('Authentication') || error.message.includes('Unauthorized') || error.message.includes('exp')) {
          if (import.meta.env.DEV) console.log(`Retrying upload for ${type}. Retries left: ${retries}`);
          return uploadFile(fileUpload, retries - 1);
        }
      }
      
      toast({
        title: "Upload Error",
        description: error instanceof Error ? error.message : `Failed to upload ${type} file`,
        variant: "destructive",
      });
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate beat upload data using schema
      const validation = beatUploadSchema.safeParse({
        title: title.trim(),
        producer: producer.trim(),
        genre,
        bpm: parseInt(bpm),
        mp3_lease_price: parseFloat(mp3LeasePrice),
        wav_lease_price: parseFloat(wavLeasePrice),
        flp_price: parseFloat(flpPrice),
        semi_exclusive_price: parseFloat(semiExclusivePrice),
        full_exclusive_price: parseFloat(fullExclusivePrice),
      });

      if (!validation.success) {
        toast({
          title: "Validation Error",
          description: validation.error.errors[0].message,
          variant: "destructive",
        });
        return;
      }

      // Validate files using helper functions
      for (const fileUpload of files) {
        if (fileUpload.type === 'image') {
          const imageValidation = validateImageFile(fileUpload.file);
          if (!imageValidation.valid) {
            toast({
              title: "Validation Error",
              description: imageValidation.error,
              variant: "destructive",
            });
            return;
          }
        } else if (fileUpload.type === 'mp3' || fileUpload.type === 'wav') {
          const audioValidation = validateAudioFile(fileUpload.file, fileUpload.type);
          if (!audioValidation.valid) {
            toast({
              title: "Validation Error",
              description: audioValidation.error,
              variant: "destructive",
            });
            return;
          }
        } else if (fileUpload.type === 'flp') {
          const projectValidation = validateProjectFile(fileUpload.file);
          if (!projectValidation.valid) {
            toast({
              title: "Validation Error",
              description: projectValidation.error,
              variant: "destructive",
            });
            return;
          }
        }
      }

      // Validate required files
      const hasImage = files.some(f => f.type === 'image');
      const hasAudio = files.some(f => f.type === 'mp3' || f.type === 'wav');
      
      if (!hasImage) {
        toast({
          title: "Validation Error",
          description: "Cover image is required",
          variant: "destructive",
        });
        return;
      }
      
      if (!hasAudio) {
        toast({
          title: "Validation Error",
          description: "At least one audio file (MP3 or WAV) is required",
          variant: "destructive",
        });
        return;
      }

      if (import.meta.env.DEV) console.log('Starting beat upload process with files:', files);
      
      // Upload all files
      const uploadPromises = files.map(uploadFile);
      const uploadedUrls = await Promise.all(uploadPromises);

      if (import.meta.env.DEV) console.log('Upload results:', uploadedUrls);

      // Check if any uploads failed
      const failedUploads = uploadedUrls.filter(url => url === null);
      if (failedUploads.length > 0) {
        if (import.meta.env.DEV) console.error(`Failed uploads count: ${failedUploads.length}`);
        toast({
          title: "Upload Error",
          description: `${failedUploads.length} file(s) failed to upload. Please try again.`,
          variant: "destructive",
        });
        return;
      }

      // Create URL mapping
      const urls: Record<string, string> = {};
      const fullUrls: Record<string, string> = {};
      
      files.forEach((fileUpload, index) => {
        const uploadResult = uploadedUrls[index];
        if (uploadResult) {
          if (typeof uploadResult === 'string') {
            urls[`${fileUpload.type}_url`] = uploadResult;
          } else {
            urls[`${fileUpload.type}_url`] = uploadResult.url;
            if (uploadResult.fullUrl) {
              fullUrls[`${fileUpload.type}_full_url`] = uploadResult.fullUrl;
            }
          }
        }
      });

      // Auto-adjust pricing based on available files and free beat setting
      const adjustedMp3Price = urls.mp3_url ? (isFreebeat ? 0 : parseFloat(mp3LeasePrice)) : null;
      const adjustedWavPrice = urls.wav_url ? (isFreebeat ? 0 : parseFloat(wavLeasePrice)) : null;
      const adjustedFlpPrice = urls.flp_url ? (isFreebeat ? 0 : parseFloat(flpPrice)) : null;
      
      // Use state values instead of FormData
      const beatData = {
        title: title.trim(),
        producer: producer.trim(),
        genre: genre,
        bpm: parseInt(bpm),
        mp3_lease_price: adjustedMp3Price,
        wav_lease_price: adjustedWavPrice,
        flp_price: adjustedFlpPrice,
        semi_exclusive_price: isFreebeat ? 0 : parseFloat(semiExclusivePrice),
        full_exclusive_price: isFreebeat ? 0 : parseFloat(fullExclusivePrice),
        is_featured: isFeatured,
        release_date: releaseDate ? new Date(releaseDate).toISOString() : null,
        exclusive_release_until: exclusiveReleaseUntil ? new Date(exclusiveReleaseUntil).toISOString() : null,
        image_url: urls.image_url || null,
        image_full_url: fullUrls.image_full_url || urls.image_url || null,
        mp3_url: urls.mp3_url || null,
        wav_url: urls.wav_url || null,
        flp_url: urls.flp_url || null,
      };

      if (import.meta.env.DEV) console.log('Inserting beat data:', beatData);
      
      const { error } = await supabase
        .from("beats")
        .insert([beatData]);

      if (error) {
        if (import.meta.env.DEV) console.error('Database insertion error:', error);
        toast({
          title: "Database Error",
          description: `Failed to save beat: ${error.message}`,
          variant: "destructive",
        });
      } else {
        if (import.meta.env.DEV) console.log('Beat successfully added to database');
        toast({
          title: "Success",
          description: "Beat added successfully",
        });
        onBeatAdded();
        onClose();
        // Reset form
        setFiles([]);
        setTitle("");
        setProducer("");
        setGenre("");
        setBpm("");
        setMp3LeasePrice("25");
        setWavLeasePrice("35");
        setFlpPrice("100");
        setSemiExclusivePrice("250");
        setFullExclusivePrice("500");
        setIsFeatured(false);
        setReleaseDate("");
        setExclusiveReleaseUntil("");
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Upload process error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    if (currentImageIndex >= 0) {
      setFiles(prev => {
        const newFiles = [...prev];
        newFiles[currentImageIndex] = {
          ...newFiles[currentImageIndex],
          croppedBlob
        };
        return newFiles;
      });
    }
    setCropperOpen(false);
  };

  const getFileIcon = (type: FileUpload['type']) => {
    if (type === 'image') return <Image className="h-4 w-4" />;
    return <Music className="h-4 w-4" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Beat</DialogTitle>
          <DialogDescription>
            Upload your beat files and set the pricing and details.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload Areas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <DropZone
              type="audio"
              title="Audio Files"
              description="MP3 & WAV Files"
              accept=".mp3,.wav"
              icon={<Music className="h-8 w-8 text-primary" />}
              files={files}
              onDrop={processFiles}
              onRemove={removeFile}
            />
            
            <DropZone
              type="image"
              title="Cover Image"
              description="PNG, JPG Files"
              accept=".png,.jpg,.jpeg"
              icon={<Image className="h-8 w-8 text-primary" />}
              files={files}
              onDrop={processFiles}
              onRemove={removeFile}
              onCropImage={(index, preview) => {
                setImageToCrop(preview);
                setCurrentImageIndex(index);
                setCropperOpen(true);
              }}
            />
            
            <DropZone
              type="flp"
              title="Project Files"
              description="FLP, ZIP, RAR Files"
              accept=".flp,.zip,.rar,.7z"
              icon={<Upload className="h-8 w-8 text-primary" />}
              files={files}
              onDrop={processFiles}
              onRemove={removeFile}
            />
          </div>

          {/* Beat Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input 
                id="title" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required 
              />
            </div>
            <div>
              <Label htmlFor="producer">Producer</Label>
              <Input 
                id="producer" 
                value={producer}
                onChange={(e) => setProducer(e.target.value)}
                required 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="genre">Genre</Label>
              <Select value={genre} onValueChange={setGenre} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select genre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Trap">Trap</SelectItem>
                  <SelectItem value="Hip Hop">Hip Hop</SelectItem>
                  <SelectItem value="R&B">R&B</SelectItem>
                  <SelectItem value="Lo-Fi">Lo-Fi</SelectItem>
                  <SelectItem value="Electronic">Electronic</SelectItem>
                  <SelectItem value="Jazz">Jazz</SelectItem>
                  <SelectItem value="Pop">Pop</SelectItem>
                  <SelectItem value="Rock">Rock</SelectItem>
                  <SelectItem value="Hyperpop">Hyperpop</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bpm">BPM</Label>
              <Input 
                id="bpm" 
                type="number" 
                min="60" 
                max="200" 
                value={bpm}
                onChange={(e) => setBpm(e.target.value)}
                required 
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Pricing</h3>
              <div className="flex items-center space-x-2">
                <Switch
                  id="free-beat"
                  checked={isFreebeat}
                  onCheckedChange={setIsFreebeat}
                />
                <Label htmlFor="free-beat">Free Beat</Label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mp3_lease_price">MP3 Lease Price ($)</Label>
                <Input 
                  id="mp3_lease_price" 
                  type="number" 
                  step="0.01" 
                  value={mp3LeasePrice}
                  onChange={(e) => setMp3LeasePrice(e.target.value)}
                  disabled={isFreebeat}
                  placeholder={isFreebeat ? "Free" : "25.00"}
                />
              </div>
              <div>
                <Label htmlFor="wav_lease_price">WAV Lease Price ($)</Label>
                <Input 
                  id="wav_lease_price" 
                  type="number" 
                  step="0.01" 
                  value={wavLeasePrice}
                  onChange={(e) => setWavLeasePrice(e.target.value)}
                  disabled={isFreebeat}
                  placeholder={isFreebeat ? "Free" : "35.00"}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="flp_price">FLP Price ($)</Label>
                <Input 
                  id="flp_price" 
                  type="number" 
                  step="0.01" 
                  value={flpPrice}
                  onChange={(e) => setFlpPrice(e.target.value)}
                  disabled={isFreebeat}
                  placeholder={isFreebeat ? "Free" : "100.00"}
                />
              </div>
              <div>
                <Label htmlFor="semi_exclusive_price">Semi Exclusive ($)</Label>
                <Input 
                  id="semi_exclusive_price" 
                  type="number" 
                  step="0.01" 
                  value={semiExclusivePrice}
                  onChange={(e) => setSemiExclusivePrice(e.target.value)}
                  disabled={isFreebeat}
                  placeholder={isFreebeat ? "Free" : "250.00"}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="full_exclusive_price">Full Exclusive ($)</Label>
              <Input 
                id="full_exclusive_price" 
                type="number" 
                step="0.01" 
                value={fullExclusivePrice}
                onChange={(e) => setFullExclusivePrice(e.target.value)}
                disabled={isFreebeat}
                placeholder={isFreebeat ? "Free" : "500.00"}
              />
            </div>
          </div>

          {/* Release Timing */}
          <div className="space-y-3">
            <h3 className="font-medium">Release Timing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="release_date">Release Date (optional)</Label>
                <Input 
                  id="release_date" 
                  type="datetime-local" 
                  value={releaseDate}
                  onChange={(e) => setReleaseDate(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Beat will be hidden until this date
                </p>
              </div>
              <div>
                <Label htmlFor="exclusive_release_until">Exclusive Until (optional)</Label>
                <Input 
                  id="exclusive_release_until" 
                  type="datetime-local" 
                  value={exclusiveReleaseUntil}
                  onChange={(e) => setExclusiveReleaseUntil(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Only exclusive licenses available until this date
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch 
              id="is_featured" 
              checked={isFeatured}
              onCheckedChange={setIsFeatured}
            />
            <Label htmlFor="is_featured">Feat​ured Beat</Label>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Adding Beat..." : "Add Beat"}
          </Button>
        </form>
        
        <ImageCropper
          src={imageToCrop}
          isOpen={cropperOpen}
          onClose={() => setCropperOpen(false)}
          onCropComplete={handleCropComplete}
          aspectRatio={16 / 10}
        />
      </DialogContent>
    </Dialog>
  );
};